/**
 * v-legal-toc — wires up the table of contents on legal pages.
 *
 * Bound to the `.legal-grid` wrapper (the common ancestor of `.legal-toc` and
 * `.legal-content`). The `.toc-link` buttons are paired by position with the
 * `h2[id]` headings (TOC order matches heading order). Clicking a link smooth-
 * scrolls to its heading; a scroll-spy highlights the link of the section that
 * is currently in view. Mirrors the reference site's TOC behaviour.
 *
 * Two things keep the highlight (and its fading heart marker) from flickering:
 *   - the scroll-spy tracks the *set* of sections in view and always activates
 *     the topmost one in document order, so several entering the band at once
 *     resolve deterministically rather than fighting over the last callback;
 *   - a click wins over the spy until its smooth scroll settles, otherwise
 *     every heading scrolled past would flash active on the way to the target.
 */
const HEADER_OFFSET = 100;
const SPY_ROOT_MARGIN = '-30% 0px -55% 0px';
// How long after the last scroll event we treat the smooth scroll as finished.
const SCROLL_SETTLE_MS = 120;

export default {
  inserted(el) {
    const toc = el.querySelector('.legal-toc');
    const article = el.querySelector('.legal-content');
    if (!toc || !article) {
      return;
    }

    const links = Array.from(toc.querySelectorAll('.toc-link'));
    const sections = Array.from(article.querySelectorAll('h2[id]'));
    const pairs = links
      .map((link, index) => ({ link, section: sections[index] }))
      .filter((pair) => pair.section);
    if (!pairs.length) {
      return;
    }

    let activeSection = null;
    const setActive = (section) => {
      // Skip redundant writes so an unchanged highlight never re-triggers the
      // marker's opacity transition (the flickering heart).
      if (section === activeSection) {
        return;
      }
      activeSection = section;
      pairs.forEach((pair) => {
        pair.link.classList.toggle('active', pair.section === section);
      });
    };

    // While a click-driven smooth scroll is in flight, the spy stands down so
    // the clicked link stays highlighted instead of chasing the scroll.
    let spySuppressed = false;
    let settleTimer = null;
    const onScrollSettle = () => {
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(() => {
        spySuppressed = false;
        window.removeEventListener('scroll', onScrollSettle);
      }, SCROLL_SETTLE_MS);
    };

    // Scroll-spy — activate the topmost section currently within the band.
    const visible = new Set();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            visible.add(entry.target);
          } else {
            visible.delete(entry.target);
          }
        });
        if (spySuppressed) {
          return;
        }
        const topmost = pairs.find((pair) => visible.has(pair.section));
        if (topmost) {
          setActive(topmost.section);
        }
      },
      { rootMargin: SPY_ROOT_MARGIN, threshold: 0 }
    );
    pairs.forEach(({ section }) => observer.observe(section));

    const handlers = pairs.map(({ link, section }) => {
      const handler = (event) => {
        event.preventDefault();
        spySuppressed = true;
        setActive(section);
        window.addEventListener('scroll', onScrollSettle, { passive: true });
        // Arm the fallback so the spy resumes even if the target is already in
        // view and the click triggers no scroll at all.
        onScrollSettle();
        const top =
          section.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
        window.scrollTo({ top, behavior: 'smooth' });
      };
      link.addEventListener('click', handler);
      return { link, handler };
    });

    // Stash teardown so unbind can clean up listeners, timer and the observer.
    el.__legalTocTeardown = () => {
      observer.disconnect();
      window.clearTimeout(settleTimer);
      window.removeEventListener('scroll', onScrollSettle);
      handlers.forEach(({ link, handler }) =>
        link.removeEventListener('click', handler)
      );
    };
  },
  unbind(el) {
    if (el.__legalTocTeardown) {
      el.__legalTocTeardown();
      delete el.__legalTocTeardown;
    }
  },
};
